import { c } from 'ttag';

import { RoleNotGrantableReason } from '../../../graphql/__generated__/schema';

/**
 * A role as the journey reads it: its identity, and whether **this operator** may
 * hand it out.
 *
 * `grantableByViewer` is judged on the actor's own membership and says nothing about
 * the account: false means « this role, never », not « not on this account ». The
 * perimeter is step 2's other question, and `accessibleAccounts` answers it.
 */
export interface OnboardingRole {
    id?: string | null;
    name?: string | null;
    grantableByViewer: boolean;
    notGrantableReason?: RoleNotGrantableReason | null;
}

/**
 * Why a role is refused, in the operator's terms: **never the API's sentence**. The
 * equivalent refusal on the API side is written in English and would reach a French
 * operator as it stands; and an enumeration is not a message, so the wording is ours.
 *
 * Two lengths, because they answer two different questions and the screen asks both:
 *
 * - the **tag** answers « pourquoi celui-là », beside the greyed option, exactly as
 *   the account tree tags « Autre société » on the rows it refuses;
 * - the **rule** answers « pourquoi je vois des rôles que je ne peux pas donner »,
 *   said once next to the field rather than repeated on every option, and stated as
 *   a rule of the platform, not as a breakdown. Refused roles stay in the list: an
 *   administrator seeing two roles out of seven would read an incomplete referential.
 *
 * Attention, both are needed. The tag alone reads as an arbitrary refusal; the rule alone
 * leaves the operator guessing which of the seven rows it is about.
 */
export interface RoleRefusal {
    /** Short qualifier, printed on the disabled option. */
    tag: string;
    /** The rule behind it and the gesture out, printed once under the field. */
    rule: string;
}

export const roleRefusal = (reason: RoleNotGrantableReason | null | undefined): RoleRefusal => {
    switch (reason) {
        case RoleNotGrantableReason.PrivilegeEscalation:
            return {
                tag: c('Onboarding role refusal').t`Beyond your own rights`,
                rule: c('Onboarding role refusal')
                    .t`Some roles grant rights you do not carry yourself, and nobody grants more than they hold — so they are offered here without being pickable. The referential is complete: pick a narrower role, or ask an administrator to widen yours.`,
            };
        case RoleNotGrantableReason.MissingPermission:
            return {
                tag: c('Onboarding role refusal').t`User management right missing`,
                rule: c('Onboarding role refusal')
                    .t`You do not carry the right to administer people, so no role can be granted by you at all. The invitation would be refused the same way when sent: ask an administrator for that right.`,
            };
        case RoleNotGrantableReason.NoActiveMembership:
            // The API documents this one as unreachable through `roles`, which refuses
            // with NOT_AUTHENTICATED first. Worded anyway rather than folded into the
            // fallback: a reason we render as nothing would be a silent grey row if the
            // query ever did return it.
            return {
                tag: c('Onboarding role refusal').t`No active membership`,
                rule: c('Onboarding role refusal')
                    .t`Your session no longer carries an active membership, so no role can be granted by you. Sign in again — the draft entered so far lives in this page and will not survive it.`,
            };
        default:
            // `grantableByViewer` is false and no reason came with it. Saying « ce rôle
            // n'est pas accordable » without a why is still worth more than a row that
            // greys out in silence, and the absence is worth reporting.
            return {
                tag: c('Onboarding role refusal').t`Not grantable`,
                rule: c('Onboarding role refusal')
                    .t`Some roles are not grantable by you, and the reason did not come with them. Pick another role, and report it: the screen expects a reason next to every refusal.`,
            };
    }
};

/**
 * The rules to print under the field, one per distinct reason, or an empty list when
 * every role is grantable.
 *
 * Deduplicated because the reason is a fact about the **operator**, not about the
 * role: five refused roles share one rule, and printing it five times would bury the
 * field it annotates. Same lesson as the closed accounts of step 2, whose remedy is
 * one block under the tree rather than one button per row.
 */
export const refusalRules = (roles: OnboardingRole[]): string[] => [
    ...new Set(
        roles.filter((role) => !role.grantableByViewer).map((role) => roleRefusal(role.notGrantableReason).rule)
    ),
];

/**
 * The role behind an id, or null, including when the roles have not arrived yet.
 *
 * Attention, null on an **unknown** id is what keeps a failed or pending `roles` query from
 * turning into a refusal: nothing is known about that role, so nothing is claimed
 * about it. Only a role we hold and that says `grantableByViewer: false` refuses.
 */
export const findRole = (roles: OnboardingRole[], roleId: string | null): OnboardingRole | null =>
    (roleId && roles.find((role) => role.id === roleId)) || null;

/** Whether this exact role, once picked, may still be granted. */
export const isRefused = (roles: OnboardingRole[], roleId: string | null): boolean =>
    findRole(roles, roleId)?.grantableByViewer === false;
